'use client'

import { useRouter } from '@/lib/navigation/PageNavigation.client'
import { useIsReactNativeWebView } from '@/lib/react-native/contexts/react-native/ReactNativeProvider.client'
import handleIntent from '@/lib/react-native/services/react-native.intent.client'
import { INTENT_SAME_AS_WEB } from '@/lib/react-native/services/react-native.intent'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import type { MouseEventHandler } from 'react'
import { startTransition, useCallback, useMemo } from 'react'
import { isLinkButton, type LinkProps } from '../Link.types'

export function useLinkClickHandler(props: LinkProps): MouseEventHandler<HTMLAnchorElement> {
  const router = useRouter()
  const { __internal_start__, shouldStartProgressBar } = router
  const isInWebView = useIsReactNativeWebView()

  const shouldPreventDefault = useMemo(
    () => props.intent && isInWebView && props.intent.name !== INTENT_SAME_AS_WEB.name,
    [props.intent, isInWebView],
  )

  return useCallback<MouseEventHandler<HTMLAnchorElement>>(
    (e) => {
      // Branch used by <LinkButton /> (`isLinkButton` / `variant !== "free"` in Link.types)
      if (isLinkButton(props)) {
        const {
          isLoading,
          isDisabled,
          href,
          opensInNewTab,
          replace,
          forceProgressBar,
          target,
          onPress,
          trackingData,
          intent,
        } = props

        if (isLoading || isDisabled) {
          e.preventDefault()

          return
        }

        // this needs to be synchronous to avoid default NextLink navigation in the background when handling native intents
        if (shouldPreventDefault) {
          e.preventDefault()
        }

        handleIntent(intent, {
          whenInBrowser: () => {
            if (opensInNewTab || target === '_blank' || e.ctrlKey || e.metaKey) {
              return
            }

            e.preventDefault()

            const hrefStr = href.toString()
            const withBar = shouldStartProgressBar(hrefStr) || Boolean(forceProgressBar)

            if (replace) {
              if (withBar) {
                router.replaceWithProgressBar(hrefStr)
              } else {
                router.replace(hrefStr)
              }
            } else if (withBar) {
              router.pushWithProgressBar(hrefStr)
            } else {
              router.push(hrefStr)
            }
          },
        })

        onPress?.(e)

        if (trackingData) {
          startTransition(() => {
            pushToGtmDataLayer(trackingData)
          })
        }
      } else {
        // Branch used by <LinkText /> and consumers of `variant="free"` (e.g. <NavigationCard />)
        const { href, opensInNewTab, onPress, trackingData, intent } = props

        // this needs to be synchronous for native intents to avoid default NextLink navigation in the background
        if (shouldPreventDefault) {
          e.preventDefault()
        }

        handleIntent(intent, {
          whenInBrowser: () => {
            if (!opensInNewTab && shouldStartProgressBar(href.toString())) {
              __internal_start__()
            }

            onPress?.(e)
          },
        })

        if (trackingData) {
          startTransition(() => {
            pushToGtmDataLayer(trackingData)
          })
        }
      }
    },
    [props, router, shouldStartProgressBar, __internal_start__, shouldPreventDefault],
  )
}
